import { SupportedLocale } from "@turo/urls";
import { parse } from "query-string";

import config from "../config";
import { EMAIL_LOGIN_STEPS } from "../constants/otp";

export const CLIENT_REQUEST_ERROR = "client_request_error";

function allowlistAndReduce<T>(
  fields: Record<string, unknown>,
  allowlist: string[] | undefined,
  reducer: Parameters<typeof Array.prototype.reduce<T>>[0],
  initialValue: T
) {
  return Object.keys(fields)
    .filter((field) => !allowlist || allowlist.includes(field))
    .filter(
      (field) =>
        typeof fields[field] === "string" ||
        typeof fields[field] === "boolean" ||
        typeof fields[field] === "number" ||
        !!fields[field]
    )
    .reduce(reducer, initialValue);
}

/** This type was defined to satisfy initial requirements. We may have to extend the value types. */
type FormDataParameters = Record<string, string | string[] | File>;

/**
 * Build a FormData object given a list of accepted fields and an object
 * with the fields
 * @param fields Object with fields and values
 * @param [allowlist] List with names of valid fields
 * @returns {FormData} Resulting FormData
 */
export function createFormData(
  fields: FormDataParameters,
  allowlist?: string[]
) {
  return allowlistAndReduce(
    fields,
    allowlist,
    (formData: FormData, field: string) => {
      const fieldsValue = fields[field];

      if (Array.isArray(fieldsValue)) {
        fieldsValue.forEach((value) =>
          formData.append(field, value.toString())
        );
      } else if (fieldsValue instanceof File) {
        formData.append(field, fieldsValue);
      } else {
        formData.append(field, fieldsValue.toString());
      }
      return formData;
    },
    new FormData()
  );
}

/**
 * Creates a standard message to pass on postMessage
 * @returns function that returns a {type, payload} object.
 */
export function buildAction<T extends string>(type: T) {
  // @ts-expect-error -- We should probably return an `undefined` payload if there is no data instead of {}
  // eslint-disable-next-line @typescript-eslint/ban-types -- {} is the default value if not provided, and is acceptable for extending in generics.
  return <U extends {}>(payload: U = {}) => ({
    payload,
    type,
  });
}

/* eslint-disable no-console */
/**
 * Creates a dispatch function to inject in iframe views
 */
export function dispatch(action: { payload: unknown; type: string }) {
  /** If test, only log since test suite is set up accordingly */
  if (process.env.NODE_ENV === "test") {
    console.group(`iframe dispatch ${action.type}`);
    console.log(action);
    console.groupEnd();
    return;
  }

  /** If production, do not log to console */
  if (process.env.NODE_ENV === "production") {
    return window.parent.postMessage(action, "*");
  }

  /** If development, log to console and use postMessage */
  if (process.env.NODE_ENV === "development") {
    console.group(`iframe dispatch ${action.type}`);
    console.log(action);
    console.groupEnd();
    window.parent.postMessage(action, "*");
  }
}

/**
 * Given a locale, get the locale to use for translations. We support more languages than translations,
 * so we need to be able to map those locales without translations to one with translations (en_CA -> en_US)
 * @param locale Current locale
 * @returns translations locale for the given locale or DEFAULT_LOCALE
 */
export function getTranslationsLocale(locale: SupportedLocale) {
  return config.LOCALE_TO_TRANSLATIONS_LOCALE[locale] || config.DEFAULT_LOCALE;
}

/**
 * @turo/authentication will pass the initial email login step as a query param
 * @see {https://github.com/turo/web-authentication/blob/4675b6dfa2e144eb76626a28a0564e6b991c6532/src/LoginOTP.tsx#L225}
 */
function isOuiCarInitialLoginStep(
  string: string
): string is typeof EMAIL_LOGIN_STEPS.LOGIN_OTP_FORM {
  return string === EMAIL_LOGIN_STEPS.LOGIN_OTP_FORM;
}

/**
 * We want to forward OuiCar users to LOGIN_OTP_FORM from direct links they will be receiving via email,
 * which we are codifying in the `initialEmailLoginStep` in `@turo/authentication`.
 * @see {https://github.com/turo/web-authentication/blob/4675b6dfa2e144eb76626a28a0564e6b991c6532/src/LoginOTP.tsx#L225}
 */
function getInitialEmailLoginStep(
  initialEmailLoginStep: string | (string | null)[] | null
) {
  return typeof initialEmailLoginStep === "string" &&
    isOuiCarInitialLoginStep(initialEmailLoginStep)
    ? initialEmailLoginStep
    : undefined;
}

/**
 * Parses the query props.
 *
 * TODO-TS: We should consider narrowing the return types from
 * `string | (string | null)[] | null` to `string | null`
 * via runtime guards.
 */
export function getPropertiesFromQuery() {
  const {
    email,
    firstName,
    initialEmailLoginStep,
    isWhite = false,
    lastName,
    locale = config.DEFAULT_LOCALE,
    referrer,
  } = parse(window.location.search.replace("?", ""));

  return {
    isWhite: isWhite === "true",
    initialEmailLoginStep: getInitialEmailLoginStep(initialEmailLoginStep),
    locale,
    referrer,
    firstName,
    lastName,
    email,
  };
}

/**
 * This method wraps a call to fetch and it normalizes the promise to
 * always returning an object to handle.
 */
export function post<T, U>(
  url: string,
  parameters?: FormDataParameters,
  headers?: Record<string, string>
) {
  return fetch(url, {
    body: parameters ? createFormData(parameters) : undefined,
    credentials: "include",
    headers,
    method: "POST",
  })
    .then((response) => response.json() as Promise<T>)
    .catch(
      (error) =>
        ({
          error: CLIENT_REQUEST_ERROR,
          errors: [error as U],
        } as const)
    );
}

interface PostJsonOptions {
  /**
   * JSON body to send. Note we type it loosely because `postJSON` already uses generics for the
   * success and error responses. We cannot automatically type `body` with generics without requiring
   * an explicit type argument, which is not ideal. TypeScript 5.2 may resolve this issue:
   *
   * @see https://www.totaltypescript.com/type-argument-placeholders-typescript-5-2-most-discussed-feature
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- See above comment.
  body: Record<string, any>;
  endpoint: string;
  /** Extra headers to send */
  headers?: Record<string, string>;
}

/**
 * Like `post`, but sends JSON instead of form data.
 * Defined as a separate method to not add complexity to `post`.
 */
export function postJSON<T, U>({ body, endpoint, headers }: PostJsonOptions) {
  return fetch(endpoint, {
    body: JSON.stringify(body),
    credentials: "include",
    headers: {
      ...headers,
      "Content-Type": "application/json",
    },
    method: "POST",
  })
    .then((response) => response.json() as Promise<T>)
    .catch(
      (error) =>
        ({
          error: CLIENT_REQUEST_ERROR,
          errors: [error as U],
        } as const)
    );
}

/**
 * This method wraps a GET call to fetch and it normalizes the promise to
 * always returning an object to handle.
 */
export function get<T, U>(url: string) {
  return fetch(url)
    .then((response) => response.json() as Promise<T>)
    .catch(
      (error) =>
        ({
          error: CLIENT_REQUEST_ERROR,
          errors: [error as U],
        } as const)
    );
}
